import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';

const LEAVE_META = {
  vacation: { label: 'Vacation', emoji: '🏖️', color: '#1565c0', bg: '#e3f2fd' },
  sick:     { label: 'Sick Day', emoji: '🤒',  color: '#c62828', bg: '#fce4ec' },
  personal: { label: 'Personal Day', emoji: '🧘‍♀️', color: '#6a1fa2', bg: '#f3e5f5' },
};

const STATUS_META = {
  pending:  { color: '#e65100', bg: '#fff3e0', label: 'Pending' },
  approved: { color: '#1b5e20', bg: '#e8f5e9', label: 'Approved' },
  refused:  { color: '#b71c1c', bg: '#ffebee', label: 'Declined' },
};

function fmtDate(d) {
  return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function LeaveRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isManager, setIsManager] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [refuseId, setRefuseId] = useState(null);
  const [refuseReason, setRefuseReason] = useState('');
  const [processing, setProcessing] = useState(null);
  const { currentUser } = useCurrentUser();

  // Skip the manager-view probe for users we already know are non-managers
  // (a team member whose teamRole is a plain 'user'/'member' and isn't the
  // owner). Leave management is admin/super_admin only, so this never hides the
  // section from an actual manager — and it avoids a guaranteed 403 in console.
  const definitelyNotManager = !!currentUser?.teamId
    && ['user', 'member'].includes(String(currentUser?.teamRole || '').toLowerCase())
    && !currentUser?.isTeamOwner;

  const load = useCallback(async () => {
    if (definitelyNotManager) { setIsManager(false); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api(`/api/leave/requests?status=${filter}`);
      if (res.status === 403) { setIsManager(false); setLoading(false); return; }
      if (res.ok) {
        const data = await res.json();
        setIsManager(true);
        setRequests(data.requests || []);
      }
    } catch { /* silently skip if not a team user */ }
    setLoading(false);
  }, [filter, definitelyNotManager]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setProcessing(id);
    try {
      const res = await api(`/api/leave/requests/${id}/approve`, { method: 'PUT' });
      if (res.ok) load();
      else { const d = await res.json(); alert(d.error || 'Failed to approve'); }
    } catch { alert('Failed to approve'); }
    setProcessing(null);
  };

  const refuse = async (id) => {
    setProcessing(id);
    try {
      const res = await api(`/api/leave/requests/${id}/refuse`, {
        method: 'PUT',
        body: JSON.stringify({ reason: refuseReason }),
      });
      if (res.ok) { setRefuseId(null); setRefuseReason(''); load(); }
      else { const d = await res.json(); alert(d.error || 'Failed to decline'); }
    } catch { alert('Failed to decline'); }
    setProcessing(null);
  };

  // Non-managers see nothing; section is hidden entirely
  if (!loading && !isManager) return null;

  return (
    <div className="dashboard-box" style={{ marginTop: '1rem', marginBottom: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0 }}>Leave Requests</h3>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          {['pending', 'approved', 'refused'].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              style={{
                padding: '0.25rem 0.65rem', borderRadius: 6, fontSize: '0.78rem',
                fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize',
                border: `1.5px solid ${filter === s ? '#0C2D48' : '#e0e3e8'}`,
                background: filter === s ? '#0C2D48' : '#fff',
                color: filter === s ? '#fff' : '#666',
                fontFamily: "'Raleway', sans-serif",
              }}
            >
              {s === 'refused' ? 'Declined' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p style={{ color: '#aaa', fontSize: '0.88rem', margin: 0 }}>Loading…</p>
      ) : requests.length === 0 ? (
        <p style={{ color: '#aaa', margin: 0, fontSize: '0.88rem' }}>
          No {filter === 'refused' ? 'declined' : filter} leave requests.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {requests.map(req => {
            const lm = LEAVE_META[req.type] || LEAVE_META.vacation;
            const sm = STATUS_META[req.status] || STATUS_META.pending;
            const days = Number(req.daysRequested);
            const usedDays = Number(req.teamMember?.usedDays || 0);
            const total = req.teamMember?.totalAllowance || 28;
            const remainingAfter = req.type !== 'sick' ? Math.max(0, total - usedDays - days) : null;

            return (
              <div key={req.id} style={{ border: '1.5px solid #e8eaed', borderRadius: 10, padding: '1rem', background: '#fafbfc' }}>
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: req.user?.color || '#0C2D48',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 800, fontSize: '0.88rem', flexShrink: 0,
                  }}>
                    {(req.user?.name || '?')[0].toUpperCase()}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.9rem', fontFamily: "'Raleway', sans-serif" }}>
                        {req.user?.name}
                      </span>
                      <span style={{ padding: '0.15rem 0.5rem', borderRadius: 12, fontSize: '0.73rem', fontWeight: 700, background: lm.bg, color: lm.color }}>
                        {lm.emoji} {lm.label}
                      </span>
                      <span style={{ padding: '0.15rem 0.5rem', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, background: sm.bg, color: sm.color }}>
                        {sm.label}
                      </span>
                    </div>

                    <div style={{ fontSize: '0.83rem', color: '#555', fontFamily: "'Raleway', sans-serif" }}>
                      {fmtDate(req.startDate)} – {fmtDate(req.endDate)}
                      {' · '}
                      <strong>{days} working day{days !== 1 ? 's' : ''}</strong>
                      {remainingAfter !== null && (
                        <span style={{ marginLeft: '0.5rem', color: remainingAfter >= 0 ? '#1b5e20' : '#b71c1c', fontWeight: 600 }}>
                          · {remainingAfter} remaining after
                        </span>
                      )}
                      {req.type !== 'sick' && (
                        <span style={{ marginLeft: '0.5rem', color: '#aaa' }}>
                          ({total - usedDays} of {total} currently available)
                        </span>
                      )}
                    </div>

                    {req.note && (
                      <div style={{ fontSize: '0.8rem', color: '#777', marginTop: '0.3rem', fontStyle: 'italic', fontFamily: "'Raleway', sans-serif" }}>
                        "{req.note}"
                      </div>
                    )}
                    {req.managerNote && (
                      <div style={{ fontSize: '0.8rem', color: '#c62828', marginTop: '0.25rem', fontFamily: "'Raleway', sans-serif" }}>
                        Reason: {req.managerNote}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action area — pending only */}
                {req.status === 'pending' && (
                  <div style={{ borderTop: '1px solid #f0f2f5', paddingTop: '0.65rem', marginTop: '0.75rem' }}>
                    {refuseId === req.id ? (
                      <div>
                        <textarea
                          placeholder="Reason for declining (optional — sent to the employee)"
                          value={refuseReason}
                          onChange={e => setRefuseReason(e.target.value)}
                          rows={2}
                          style={{
                            width: '100%', border: '1.5px solid #e0e3e8', borderRadius: 8,
                            padding: '0.5rem 0.75rem', fontFamily: "'Raleway', sans-serif",
                            fontSize: '0.85rem', boxSizing: 'border-box', resize: 'vertical',
                            marginBottom: '0.5rem',
                          }}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => refuse(req.id)}
                            disabled={processing === req.id}
                            style={{ padding: '0.4rem 1rem', borderRadius: 7, border: 'none', background: '#c62828', color: '#fff', fontWeight: 700, fontSize: '0.83rem', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", opacity: processing === req.id ? 0.6 : 1 }}
                          >
                            {processing === req.id ? 'Declining…' : 'Confirm Decline'}
                          </button>
                          <button
                            onClick={() => { setRefuseId(null); setRefuseReason(''); }}
                            style={{ padding: '0.4rem 0.85rem', borderRadius: 7, border: '1.5px solid #dde3ea', background: '#fff', fontWeight: 600, fontSize: '0.83rem', cursor: 'pointer', color: '#888', fontFamily: "'Raleway', sans-serif" }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => approve(req.id)}
                          disabled={!!processing}
                          style={{ padding: '0.4rem 1.1rem', borderRadius: 7, border: 'none', background: '#1b5e20', color: '#fff', fontWeight: 700, fontSize: '0.83rem', cursor: processing ? 'not-allowed' : 'pointer', fontFamily: "'Raleway', sans-serif", opacity: processing ? 0.6 : 1 }}
                        >
                          {processing === req.id ? 'Approving…' : 'Approve'}
                        </button>
                        <button
                          onClick={() => setRefuseId(req.id)}
                          disabled={!!processing}
                          style={{ padding: '0.4rem 1rem', borderRadius: 7, border: '1.5px solid #c62828', background: '#fff', color: '#c62828', fontWeight: 700, fontSize: '0.83rem', cursor: processing ? 'not-allowed' : 'pointer', fontFamily: "'Raleway', sans-serif", opacity: processing ? 0.6 : 1 }}
                        >
                          Decline
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default LeaveRequests;
